<script lang="ts">
  /* The advisory blocks — the required ones and the alarming ones.
   *
   * ═══ `fine` IS LOAD-BEARING AND MAY NOT BE MADE LOUDER ═══
   *
   * The CHS notice and the OGL-Canada acknowledgement are the conditions the
   * public ENC proxy is licensed under, and the advisory wording is the reason
   * a depth-only router is allowed to draw a line at all. They may not be
   * shortened, hidden, or moved out of the panel they belong to.
   *
   * But required is not the same as loud. Set at body size the ~430-character
   * CHS notice rendered as eleven lines of monospace — measured on a phone, a
   * third of the whole route panel, pushing the controls it belongs under off
   * the fold. As 9 px sans in `--sub` it is six lines and reads as a notice
   * rather than as the panel's main text.
   *
   * ═══ AND `warn` IS NOT DECORATION EITHER ═══
   *
   * "Stops at leg 3: the stream there is stronger than the boat can make good"
   * is the router refusing, and a schedule that states a slack to the minute
   * invites more trust than a line on a chart does. Amber, boxed, in prose.
   */
  import type { Snippet } from 'svelte';

  let {
    /** `fine` — required small print, subordinate by design.
     *  `warn`  — the plan has a problem you can still act on.
     *  `bad`   — the data ran out; nothing here can be trusted. */
    tone = 'fine',
    children
  }: { tone?: 'fine' | 'warn' | 'bad'; children: Snippet } = $props();
</script>

{#if tone === 'fine'}
  <p class="fine">{@render children()}</p>
{:else}
  <p class="box" class:bad={tone === 'bad'} role="status">{@render children()}</p>
{/if}

<style>
  .fine {
    margin: var(--sp-5) 0 0;
    font: var(--fs-micro) / var(--lh-read) var(--sans);
    color: var(--sub);
  }
  .box {
    margin: var(--sp-4) 0 0;
    padding: var(--sp-4);
    border-radius: var(--r-2);
    background: var(--am-weak);
    border: 1px solid var(--am-line);
    font: var(--fs-read) / var(--lh-read) var(--sans);
    color: var(--am);
  }
  .box.bad {
    background: var(--red-weak);
    border-color: var(--red-line);
    color: var(--red);
  }
</style>
